import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, Plus, Eye, Pencil, Trash2, Package as PackageIcon, Ruler, Weight, Beaker } from "lucide-react";

const MTYPE_ICON = { SIZE: Ruler, WEIGHT: Weight, VOLUME: Beaker };

function MeasurementSummary({ p }) {
  if (p.measurement_type === "SIZE") {
    return <span>{p.length} × {p.width} × {p.height} {p.size_unit}</span>;
  }
  if (p.measurement_type === "WEIGHT") {
    return <span>{p.weight_value} {p.weight_unit}</span>;
  }
  if (p.measurement_type === "VOLUME") {
    return <span>{p.volume_value} {p.volume_unit}</span>;
  }
  return <span>—</span>;
}

export default function Products() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/store/products", { params: { search: search || undefined } })
      .then(r => setRows(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/store/products/${confirmDelete.id}`);
      toast.success("Product deleted.");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="products-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-2">/ Products</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Your Products</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">Only you can see and edit these.</p>
        </div>
        <Link to="/store/products/new">
          <Button data-testid="add-product-btn" className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium">
            <Plus className="w-4 h-4 mr-2" /> Add Product
          </Button>
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            data-testid="products-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search products…"
            className="pl-10 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white focus-visible:ring-cyan-400"
          />
        </div>
        <div className="text-xs font-mono-tech text-slate-500">
          {loading ? "Loading…" : `${rows.length} item${rows.length === 1 ? "" : "s"}`}
        </div>
      </div>

      {rows.length === 0 && !loading ? (
        <div className="border border-dashed border-slate-200 dark:border-slate-800 rounded-lg py-20 text-center bg-slate-100/30 dark:bg-slate-900/30">
          <PackageIcon className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <div className="font-display text-lg text-slate-700 dark:text-slate-300 mb-1">No products yet</div>
          <div className="text-sm text-slate-500 mb-5">Add your first product to start generating QR codes.</div>
          <Link to="/store/products/new">
            <Button className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium">
              <Plus className="w-4 h-4 mr-2" /> Add Product
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {rows.map(p => {
            const Icon = MTYPE_ICON[p.measurement_type] || PackageIcon;
            return (
              <div
                key={p.id}
                data-testid={`product-card-${p.id}`}
                className="border border-slate-200 dark:border-slate-800 rounded-lg bg-slate-100/40 dark:bg-slate-900/40 overflow-hidden flex flex-col hover:border-slate-300 hover:dark:border-slate-700 transition-colors"
              >
                <div className="aspect-square bg-white dark:bg-slate-950 grid place-items-center border-b border-slate-200 dark:border-slate-800 overflow-hidden">
                  {p.photo ? (
                    <img src={p.photo} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <PackageIcon className="w-12 h-12 text-slate-700" />
                  )}
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="font-display text-base font-semibold text-slate-900 dark:text-white truncate">{p.name}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2 mt-1">{p.description || "—"}</div>
                  <div className="mt-3 flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-300">
                    <Icon className="w-3.5 h-3.5 text-cyan-400" />
                    <MeasurementSummary p={p} />
                  </div>
                  <div className="text-[10px] text-slate-500 mt-2 font-mono-tech">
                    {new Date(p.created_at).toLocaleDateString()}
                  </div>
                  <div className="mt-4 flex items-center gap-1.5 pt-3 border-t border-slate-200/60 dark:border-slate-800/60">
                    <Button
                      data-testid={`edit-product-${p.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/store/products/edit/${p.id}`)}
                      className="h-8 flex-1 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-200 hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
                    >
                      <Pencil className="w-3 h-3 mr-1.5" /> Edit
                    </Button>
                    <Button
                      data-testid={`delete-product-${p.id}`}
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirmDelete(p)}
                      className="h-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-6">
          <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="font-display text-lg font-semibold mb-2">Delete this product?</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              <span className="text-slate-900 dark:text-white">{confirmDelete.name}</span> will be permanently removed.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                data-testid="delete-cancel-btn"
                variant="outline"
                onClick={() => setConfirmDelete(null)}
                className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-200 hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
              >
                Cancel
              </Button>
              <Button
                data-testid="delete-confirm-btn"
                onClick={onDelete}
                className="bg-red-500 text-slate-900 dark:text-white hover:bg-red-400"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
